import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useLanguage } from '@/lib/context';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Loader2, CheckCircle, Clock, XCircle, MapPin, Calendar,
  Users, Mail, Phone, Home, ChevronLeft, Copy, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { 
    icon: Clock, 
    color: 'text-yellow-600 bg-yellow-50', 
    badge: 'bg-yellow-100 text-yellow-700',
    label: 'Pending Approval' 
  },
  confirmed: { 
    icon: CheckCircle, 
    color: 'text-green-600 bg-green-50',
    badge: 'bg-green-100 text-green-700',
    label: 'Confirmed' 
  },
  declined: { 
    icon: XCircle, 
    color: 'text-red-600 bg-red-50',
    badge: 'bg-red-100 text-red-700',
    label: 'Declined' 
  },
  cancelled: { 
    icon: XCircle, 
    color: 'text-gray-600 bg-gray-50',
    badge: 'bg-gray-100 text-gray-700',
    label: 'Cancelled' 
  },
  completed: { 
    icon: CheckCircle, 
    color: 'text-blue-600 bg-blue-50',
    badge: 'bg-blue-100 text-blue-700',
    label: 'Completed' 
  }
};

const AccommodationBookingPage = () => {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const { t } = useLanguage();
  
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (bookingId && email) {
      fetchBooking();
    } else {
      setError('Missing booking information');
      setLoading(false);
    }
  }, [bookingId, email]);

  const fetchBooking = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/accommodation/bookings/${bookingId}?email=${encodeURIComponent(email)}`
      );
      
      if (!response.ok) {
        if (response.status === 404) {
          setError('Booking not found');
        } else {
          setError('Failed to load booking');
        }
        return;
      }
      
      const data = await response.json();
      setBooking(data);
    } catch (err) {
      console.error('Error fetching booking:', err);
      setError('Failed to load booking');
    } finally {
      setLoading(false);
    }
  };

  const copyBookingId = () => {
    navigator.clipboard.writeText(bookingId);
    toast.success('Booking ID copied!');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-purple-500 mx-auto mb-4" />
          <p className="text-gray-500">Loading booking...</p>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-gray-50">
        <XCircle className="h-16 w-16 text-gray-300" />
        <h2 className="text-xl font-semibold text-gray-700">{error}</h2>
        <p className="text-gray-500">Please check your booking confirmation email for the correct link.</p>
        <Button asChild>
          <Link to="/accommodation">
            <ChevronLeft className="h-4 w-4 mr-2" />
            Browse Accommodations
          </Link>
        </Button>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[booking.status] || STATUS_CONFIG.pending;
  const StatusIcon = statusConfig.icon;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/accommodation">
              <ChevronLeft className="h-4 w-4 mr-1" />
              Browse Accommodations
            </Link>
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Status Banner */}
        <Card className={`mb-6 ${statusConfig.color}`}>
          <CardContent className="py-6">
            <div className="flex items-center gap-4">
              <StatusIcon className="h-12 w-12" />
              <div>
                <h1 className="text-2xl font-bold">
                  {booking.status === 'confirmed' 
                    ? 'Booking Confirmed!' 
                    : booking.status === 'pending'
                    ? 'Booking Request Sent'
                    : `Booking ${statusConfig.label}`
                  }
                </h1>
                <p className="text-sm opacity-80 mt-1">
                  {booking.status === 'confirmed' 
                    ? 'Your accommodation is reserved. Check your email for details.'
                    : booking.status === 'pending'
                    ? 'The host will review your request and respond shortly.'
                    : booking.host_response || ''
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Booking Details */}
          <div className="md:col-span-2 space-y-6">
            {/* Listing Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Accommodation</span>
                  <Badge className={statusConfig.badge}>{statusConfig.label}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4">
                  {booking.listing_photos?.[0] && (
                    <img 
                      src={booking.listing_photos[0]}
                      alt=""
                      className="w-24 h-24 rounded-lg object-cover"
                    />
                  )}
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{booking.listing_title}</h3>
                    <p className="text-gray-500 text-sm mt-1">
                      Booking ID: 
                      <button 
                        onClick={copyBookingId}
                        className="ml-2 font-mono text-purple-600 hover:underline inline-flex items-center gap-1"
                      >
                        {booking.booking_id}
                        <Copy className="h-3 w-3" />
                      </button>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stay Details */}
            <Card>
              <CardHeader>
                <CardTitle>Stay Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Check-in</p>
                    <p className="font-medium flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-purple-500" />
                      {booking.check_in}
                    </p>
                    {booking.status === 'confirmed' && (
                      <p className="text-xs text-gray-500 mt-1">
                        After {booking.check_in_time || '15:00'}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Check-out</p>
                    <p className="font-medium flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-purple-500" />
                      {booking.check_out}
                    </p>
                    {booking.status === 'confirmed' && (
                      <p className="text-xs text-gray-500 mt-1">
                        Before {booking.check_out_time || '11:00'}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2 text-gray-600">
                  <Users className="h-4 w-4" />
                  {booking.guests} guest{booking.guests > 1 ? 's' : ''} • {booking.nights} night{booking.nights > 1 ? 's' : ''}
                </div>

                {/* Address - Only shown when confirmed */}
                {booking.status === 'confirmed' && booking.address && (
                  <div className="pt-4 border-t">
                    <p className="text-sm text-gray-500 mb-1">Address</p>
                    <p className="font-medium flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-purple-500" />
                      {booking.address}
                    </p>
                  </div>
                )}

                {booking.status !== 'confirmed' && (
                  <p className="text-sm text-gray-500 italic">
                    Address will be provided once the host confirms your booking.
                  </p>
                )}

                {/* Check-in Instructions */}
                {booking.status === 'confirmed' && booking.check_in_instructions && (
                  <div className="pt-4 border-t">
                    <p className="text-sm text-gray-500 mb-1">Check-in Instructions</p>
                    <p className="text-gray-700 whitespace-pre-wrap">{booking.check_in_instructions}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Guest Info */}
            <Card>
              <CardHeader>
                <CardTitle>Guest Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="flex items-center gap-2">
                  <span className="text-gray-500">Name:</span>
                  <span className="font-medium">{booking.guest_name}</span>
                </p>
                <p className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <span>{booking.guest_email}</span>
                </p>
                {booking.guest_phone && (
                  <p className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span>{booking.guest_phone}</span>
                  </p>
                )}
                {booking.message && (
                  <div className="pt-2 mt-2 border-t">
                    <p className="text-sm text-gray-500">Your message:</p>
                    <p className="text-gray-700 italic">"{booking.message}"</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Host Response */}
            {booking.host_response && (
              <Card>
                <CardHeader>
                  <CardTitle>Host Message</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700">{booking.host_response}</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Price Summary Sidebar */}
          <div className="md:col-span-1">
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle>Price Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span>€{booking.nightly_rate} × {booking.nights} nights</span>
                  <span>€{(booking.nightly_rate * booking.nights).toFixed(2)}</span>
                </div>
                
                {booking.discount_amount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>{booking.discount_type} discount</span>
                    <span>-€{booking.discount_amount.toFixed(2)}</span>
                  </div>
                )}
                
                <div className="flex justify-between text-sm">
                  <span>Service fee</span>
                  <span>€{booking.service_fee.toFixed(2)}</span>
                </div>
                
                <div className="flex justify-between font-bold pt-3 border-t">
                  <span>Total</span>
                  <span>€{booking.total.toFixed(2)} {booking.currency}</span>
                </div>

                <p className="text-xs text-gray-500 pt-2">
                  Payment is collected by the host.
                </p>
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="mt-4 space-y-2">
              <Button variant="outline" className="w-full" asChild>
                <Link to={`/accommodation/${booking.listing_id}`}>
                  <Home className="h-4 w-4 mr-2" />
                  View Listing
                </Link>
              </Button>
              <Button variant="outline" className="w-full" asChild>
                <Link to="/accommodation">
                  Browse More
                  <ExternalLink className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccommodationBookingPage;
